import Head from 'next/head';
import {useRouter} from 'next/router';
import {ParsedUrlQuery} from 'querystring';
import React from 'react';

import {BrowsePageContent, CertificationTopic, PaginatedContent} from '@udemy/browse-aggregator';
import {HeadSEO, useCanonicalUrl} from '@udemy/head-seo';
import {CourseSearchQuery} from '@udemy/react-discovery-units';
import {ExperimentationProvider} from '@udemy/react-experimentation-provider';
import {SubscriptionPlanProductType, SubscriptionPlansProvider} from '@udemy/subscription-graphql';

import {getLayout} from 'src/components/layout';
import {SchemaMarkup} from 'src/components/schema-markup/schema-markup.react-component';
import {BrowseAggregatorContext} from 'src/contexts/certification-browse-page-content-context';
import {META_DEFAULT_BASE_URL} from 'src/lib/constants';
import {INDEED_LINKS_ON_AGGREGATOR_PAGES} from 'src/lib/experimentation-constants';
import {PageProps} from 'src/types/page-props';

import {AggregatorCourseDirectoryContextProvider} from '../../components/aggregator-course-directory/providers/aggregator-course-directory-context-provider.react-component';
import {useSetupNavigation} from '../../hooks/use-setup-navigation';
import {CertificationAggregator} from './certification-aggregator';

export type CertificationAggregatorPageFeatureProps = PageProps & {
    params: ParsedUrlQuery;
    browsePageContent: BrowsePageContent;
    relatedAggregatorsComponentInitialData?: PaginatedContent<BrowsePageContent>;
    certificationTopicCarouselComponentInitialData: PaginatedContent<CertificationTopic>;
    courseSearchInitialData?: CourseSearchQuery;
};

export function CertificationAggregatorPageFeature(props: CertificationAggregatorPageFeatureProps) {
    const router = useRouter();
    const {url: canonicalUrl} = useCanonicalUrl(
        router as {locale: string; asPath: string},
        [],
        META_DEFAULT_BASE_URL,
    );
    useSetupNavigation();

    const browsePageContent = props.browsePageContent;

    const metaProps = {
        title: browsePageContent.meta_title,
        description: browsePageContent.meta_description,
        seoRobotRules: [],
    };

    // And example schema markup for aggregator with main entity
    // TODO: Update this schema markup
    const schemaMarkup = undefined;

    return (
        <>
            <HeadSEO
                title={metaProps.title}
                ogTitle={metaProps.description}
                description={metaProps.title}
                ogDescription={metaProps.description}
                ogLocale={process.env.I18N_PATH_TO_BUNDLE_MAP[props.locale]}
                canonicalUrl={canonicalUrl}
                assetPath={process.env.ASSET_PREFIX}
                headWrapper={Head}
            />
            {schemaMarkup && <SchemaMarkup schemaMarkup={schemaMarkup} />}
            <BrowseAggregatorContext.Provider value={{browsePageContent}}>
                <SubscriptionPlansProvider
                    subscriptionPlanQueryVariables={[
                        {
                            filters: {
                                productType: SubscriptionPlanProductType.Consumersubscription,
                            },
                            marketingURL: `${new URL(canonicalUrl).origin}${router.asPath}`,
                        },
                    ]}
                >
                    <AggregatorCourseDirectoryContextProvider
                        initialCourseSearchResponse={props.courseSearchInitialData}
                    >
                        <ExperimentationProvider features={[INDEED_LINKS_ON_AGGREGATOR_PAGES]}>
                            <CertificationAggregator {...props} />
                        </ExperimentationProvider>
                    </AggregatorCourseDirectoryContextProvider>
                </SubscriptionPlansProvider>
            </BrowseAggregatorContext.Provider>
        </>
    );
}

CertificationAggregatorPageFeature.getLayout = getLayout;
